import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { addLocalDays, gramsToKg, localDateFromEpoch, toKilogramReps, weekdayFromLocalDate } from '@liftx/core';
import { Screen } from '../../ui/components/Screen';
import { LiftXLogo } from '../../ui/components/LiftXLogo';
import { exerciseRepository } from '../../data/repositories/exerciseRepository';
import { planRepository } from '../../data/repositories/planRepository';
import { progressRepository } from '../../data/repositories/progressRepository';
import { sessionRepository } from '../../data/repositories/sessionRepository';
import { formatExerciseName } from '../../ui/format/exerciseName';
import { ROUTES, planDetailPath, progressRecordDetailPath, workoutSessionPath, workoutSummaryPath } from '../../ui/navigation/routes';
import type { PersonalRecordRecord, SessionRecord } from '../../data/types';
import styles from './Dashboard.module.css';

const PR_LABELS: Record<string, string> = {
  rep_max: 'Rep max',
  est_1rm: 'Estimated 1RM',
  max_weight: 'Max weight',
  max_volume_set: 'Best set volume',
  max_reps: 'Max reps',
  min_assistance: 'Least assistance',
  max_duration: 'Max duration',
  max_distance: 'Max distance',
  best_pace: 'Best pace',
};

function formatRecordValue(value: number, unit: string): string {
  if (unit === 'g') return `${String(gramsToKg(value))} kg`;
  if (unit === 'g-reps') return `${toKilogramReps(value).toLocaleString()} kg-reps`;
  return `${String(value)} ${unit}`;
}

function currentLocalDate(): { localDate: string; weekday: number } {
  const tzOffsetMin = -new Date().getTimezoneOffset();
  const localDate = localDateFromEpoch(Date.now(), tzOffsetMin);
  return { localDate, weekday: weekdayFromLocalDate(localDate) };
}

interface NextDay {
  planId: string;
  dayName: string;
}

/**
 * "What do I do right now": resume card, next scheduled day, recent
 * activity, quick actions. Master Phase 8. Distinct from Progress's Overview
 * (a summary screen) by framing, not data source — see the approved plan's
 * Decision AL.
 */
export function DashboardScreen(): React.JSX.Element {
  const navigate = useNavigate();
  const [openSession, setOpenSession] = useState<SessionRecord | null | undefined>(undefined);
  const [nextDay, setNextDay] = useState<NextDay | null>(null);
  const [recentSessions, setRecentSessions] = useState<SessionRecord[]>([]);
  const [recentRecords, setRecentRecords] = useState<PersonalRecordRecord[]>([]);
  const [exerciseNames, setExerciseNames] = useState<Record<string, string>>({});
  const [weeklyVolumeKg, setWeeklyVolumeKg] = useState<number | null>(null);

  useEffect(() => {
    void (async () => {
      const { localDate, weekday } = currentLocalDate();
      const weekStart = addLocalDays(localDate, -(weekday - 1));

      const [session, next, sessions, records, weekly] = await Promise.all([
        sessionRepository.getOpenSession(),
        planRepository.getNextScheduledDay(weekday),
        progressRepository.listRecentSessions(3),
        progressRepository.listRecentPersonalRecords(3),
        progressRepository.getWeeklyVolumeGramReps(weekStart),
      ]);

      setOpenSession(session ?? null);
      setNextDay(next === null ? null : { planId: next.planId, dayName: next.dayName });
      setRecentSessions(sessions);
      setRecentRecords(records);
      setWeeklyVolumeKg(toKilogramReps(weekly.volumeGramReps));

      const names = await Promise.all(
        [...new Set(records.map((r) => r.exerciseId))].map(async (id) => {
          const exercise = await exerciseRepository.get(id);
          return [id, exercise === undefined ? id : formatExerciseName(exercise.name)] as const;
        }),
      );
      setExerciseNames(Object.fromEntries(names));
    })();
  }, []);

  async function handleFreeWorkout(): Promise<void> {
    const session = await sessionRepository.startFree();
    navigate(workoutSessionPath(session.id));
  }

  return (
    <Screen title="Dashboard" subtitle="Your training at a glance.">
      {/* Logo redesign pass: the app's one prominent logged-in logo — large,
          centred, on the Home page only. Removed from every other logged-in
          screen entirely (previously a top-left AppShell header). */}
      <div className={styles.logoWrap}>
        <LiftXLogo height={96} />
      </div>

      {openSession !== undefined && openSession !== null && (
        <div className={`${styles.card} ${styles.resumeCard}`} role="status">
          <p className={styles.cardTitle}>{openSession.name ?? 'Workout'} is {openSession.status}</p>
          <Link className={`${styles.actionButton} ${styles.actionOn}`} to={workoutSessionPath(openSession.id)}>
            Continue workout
          </Link>
        </div>
      )}

      {openSession === null && nextDay !== null && (
        <div className={styles.card}>
          <p className={styles.cardTitle}>Next up: {nextDay.dayName}</p>
          <Link className={styles.actionButton} to={planDetailPath(nextDay.planId)}>
            View plan
          </Link>
        </div>
      )}

      <div className={styles.actions}>
        <Link className={styles.actionButton} to={ROUTES.plans}>
          Choose a plan
        </Link>
        <button type="button" className={styles.actionButton} onClick={() => void handleFreeWorkout()}>
          Free workout
        </button>
        <Link className={styles.actionButton} to={ROUTES.progressMeasurements}>
          Log a measurement
        </Link>
      </div>

      <div className={styles.summaryGrid}>
        <div className={styles.section}>
          <p className={styles.sectionTitle}>This week</p>
          <div className={styles.summaryStat}>
            <span>Volume</span>
            <span>{weeklyVolumeKg === null ? '—' : `${weeklyVolumeKg.toLocaleString()} kg-reps`}</span>
          </div>
        </div>

        <div className={styles.section}>
          <p className={styles.sectionTitle}>Recent workouts</p>
          {recentSessions.length === 0 ? (
            <p role="status" className={styles.stateText}>
              Nothing logged yet.
            </p>
          ) : (
            <ul className={styles.list}>
              {recentSessions.map((session) => (
                <li key={session.id}>
                  <Link className={styles.listItemLink} to={workoutSummaryPath(session.id)}>
                    <span>{session.name ?? 'Workout'}</span>
                    <span className={styles.itemMeta}>{session.localDate}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={styles.section}>
          <p className={styles.sectionTitle}>Recent personal records</p>
          {recentRecords.length === 0 ? (
            <p role="status" className={styles.stateText}>
              Complete a workout to start setting records.
            </p>
          ) : (
            <ul className={styles.list}>
              {recentRecords.map((record) => (
                <li key={record.id}>
                  <Link className={styles.listItemLink} to={progressRecordDetailPath(record.exerciseId)}>
                    <span>
                      {exerciseNames[record.exerciseId] ?? record.exerciseId} — {PR_LABELS[record.kind] ?? record.kind}
                    </span>
                    <span className={styles.itemMeta}>{formatRecordValue(record.value, record.unit)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Screen>
  );
}
